<template>
  <div class="window-container">
    <div
      v-show="addNewRoom"
      class="float-modal"
      :style="`width:${selectNewRoomWidth}`"
    >
      <v-select
        ref="newChatSelect"
        v-model="newChatSelectedOption"
        :options="newChatSelectOptions"
        @input="createRoom()"
        v-click-outside="closeAddRoom"
      />
    </div>
    <vue-advanced-chat
      ref="chatWindow"
      :height="screenHeight"
      :styles="JSON.stringify(styles)"
      :current-user-id="currentUserId"
      :room-id="roomId"
      :rooms="JSON.stringify(rooms)"
      :rooms-loaded="roomsLoaded"
      :load-first-room="false"
      :messages="JSON.stringify(messages)"
      :messages-loaded="messageLoaded"
      :room-message="roomMessage"
      :message-actions="JSON.stringify(messageActions)"
      :room-actions="JSON.stringify(roomActions)"
      :show-new-messages-divider="false"
      :audio-bit-rate="128"
      :audio-sample-rate="44100"
      @fetch-messages="fetchMessages($event.detail[0])"
      @send-message="sendMessage($event.detail[0])"
      @delete-message="deleteMessage($event.detail[0])"
      @open-file="openFile($event.detail[0])"
      @add-room="addRoom"
      @room-action-handler="menuActionHandler($event.detail[0])"
      @menu-action-handler="menuActionHandler($event.detail[0])"
      @message-action-handler="menuActionHandler($event.detail[0])"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import chatServer from "../../server/chat";
import chatMessageServer from "../../server/chatMessage";
import portalServer from "../../server/portalAccess";
import caseLoadServer from "../../server/caseLoadRecord";
import { formatDateTimeMysql } from "../../helpers/date";
import chatSignalRConnection from "../../signalr/chat";
import { register } from "vue-advanced-chat";
register();

export default {
  name: "Messages",
  props: ["showing"],
  data() {
    return {
      rooms: [],
      roomId: "",
      roomsLoaded: true,
      roomsLoadedCount: 0,
      selectedRoom: null,
      messages: [],
      messageLoaded: true,
      roomMessage: "",
      lastLoadedMessage: null,
      previousLastLoadedMessage: null,
      typingMessageCache: "",
      addNewRoom: false,
      messageActions: [
        {
          name: "delete",
          title: "Delete Message"
        }
      ],
      roomActions: [
        {
          name: "archive",
          title: "Archive Conversations"
        }
      ],
      styles: [{ container: { borderRadius: "4px" } }],
      messagesInterval: null,
      newChatSelectOptions: [],
      newChatSelectedOption: null,
      currentUserId: null,
      portalUsers: null,
      selectNewConversationOpen: false,
      signalRisConnected: false
    };
  },

  methods: {
    ...mapActions("layout", ["showAlertSuccess", "fetchCurrentUser"]),
    async fetchRooms() {
      this.roomLoaded = false;
      this.rooms = [];

      const response = await chatServer.getAll(this.currentUser?.id);
      if (response.length > 0) {
        this.rooms = response.map(room => ({
          roomId: room.id,
          roomName: `${room.patient.mrn} - ${room.portalUser.name}`,
          avatar: room.patient.avatarId
            ? chatMessageServer.avatarUrl(room.patient.avatarId)
            : "favicon.png",
          users: [
            { _id: room.patient.id, username: this.formatUser(room.patient) },
            { _id: room.portalUser.id, username: room.portalUser.name }
          ],
          typingUsers: [this.currentUserId]
        }));

        this.roomId = this.rooms[0].roomId;
        this.roomsLoadedCount = this.rooms.length;
      }

      this.roomLoaded = true;
      await this.fetchNewChatSelectOptions();
    },
    async fetchMessages({ room }) {
      this.messageLoaded = false;

      if (this.previousLastLoadedMessage && !this.lastLoadedMessage) {
        this.messagesLoaded = true;
        return;
      }

      this.selectedRoom = room ? room.roomId : this.roomId;
      this.roomId = room ? room.roomId : this.roomId;
      if (!this.selectedRoom) {
        return;
      }

      const response = await chatMessageServer.find(this.selectedRoom);
      const msgs = [];
      if (response.length > 0) {
        response.forEach(message => {
          const formattedMessage = this.formatMessage(room, message);
          msgs.unshift(formattedMessage);
        });

        if (this.lastLoadedMessage) {
          this.previousLastLoadedMessage = this.lastLoadedMessage;
        }
        this.lastLoadedMessage = response[response.length - 1];
      }

      setTimeout(() => {
        this.messages = Object.assign([], msgs);
        this.messagesLoaded = true;
      }, 0);

      this.rooms.filter(
        x => x.roomId == this.selectedRoom
      ).lastMessage = this.lastLoadedMessage;
      this.messageLoaded = true;
    },
    formatMessage(room, message) {
      var files = [];
      if (message.fileId) {
        let file = JSON.parse(JSON.parse(JSON.stringify(message.fileData)));
        let link = chatMessageServer.downloadUrl(message.fileId);
        files = [
          {
            name: file.name,
            size: file.size,
            type: file.extension,
            audio: file.audio ? file.audio : false,
            duration: file.audio ? file.duration : null,
            url: link
          }
        ];
      }

      const formattedMessage = {
        ...message,
        ...{
          senderId:
            message.senderType == "PortalUser"
              ? message.portalUser.id
              : this.currentUserId,
          _id: message.id,
          content: message.message,
          seconds: this.formatTimeOnly(message.dateTime).seconds,
          timestamp: this.formatTimeOnly(message.dateTime),
          date: this.formatDateOnly(message.dateTime),
          username: this.formatUser(
            message.senderType == "PortalUser"
              ? message.portalUser
              : message.senderUser
          ),
          distributed: true,
          lastMessage: {
            ...message.lastMessage,
            senderId:
              message.senderType == "PortalUser"
                ? message.portalUser.id
                : message.senderUser.id
          },
          system: false,
          disableActions: false,
          disableReactions: true,
          files: files,
          seen: message.seen
        }
      };

      return formattedMessage;
    },
    async sendMessage({ content, roomId, files }) {
      const room = this.rooms.find(x => x.roomId == roomId);
      const portalAccessUser = this.portalUsers.find(
        x => x.portalUser.id == room.users[1]._id
      );

      let message = {
        SenderType: 0,
        PatientId: room.users[0]._id,
        SenderUserId: this.currentUser?.id,
        PortalUserId: room.users[1]._id,
        Message: content,
        DateTime: formatDateTimeMysql(new Date()),
        Relationship: portalAccessUser.relationship,
        Seen: false
      };

      if (files?.length > 0) {
        let messageFiles = [];
        let messageFilesData = "";
        for (let index = 0; index < files.length; index++) {
          messageFiles.unshift(this.getFileToUpload(files[index]));
        }
        messageFilesData = JSON.stringify(this.formattedFiles(files));
        message.Files = messageFiles;
        message.FilesData = messageFilesData;
      }

      await chatMessageServer.create(message);
      chatServer.sendToPortal(
        {
          tenantName: this.currentTenant,
          receivers: [message.PortalUserId]
        },
        this.signalRAppUrl
      );
      this.fetchMessages({});
    },
    getFileToUpload(file) {
      let metadata = {
        type: file.type
      };
      return new File([file.blob], file.name + "." + file.extension, metadata);
    },
    menuActionHandler({ action, message, roomId }) {
      switch (action.name) {
        case "delete":
          this.deleteMessage(message);
          break;
        case "archive":
          this.archiveRoom(roomId);
          break;
      }
    },
    async deleteMessage(message) {
      await chatMessageServer.delete(message._id);
      this.fetchMessages({});
    },
    async archiveRoom(roomId) {
      await chatServer.delete(roomId);
      await this.fetchRooms();
    },
    formattedFiles(files) {
      const formattedFiles = [];
      files.forEach(file => {
        const messageFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          extension: file.extension || file.type,
          url: file.url || file.localUrl
        };
        if (file.audio) {
          messageFile.audio = true;
          messageFile.duration = file.duration;
        }
        formattedFiles.push(messageFile);
      });
      return formattedFiles;
    },
    openFile({ file }) {
      window.open(file.file.url, "_blank");
    },
    async createRoom() {
      const access = this.newChatSelectedOption.data;

      await chatServer.create({
        PatientId: access.patient.id,
        PortalUserId: access.portalUser.id,
        UserId: this.currentUser?.id
      });

      this.addNewRoom = false;
      this.newChatSelectedOption = null;
      await this.fetchRooms();
    },
    async fetchNewChatSelectOptions() {
      let response = this.portalUsers;
      if (this.currentUser.onlyAccessAssignedPatients) {
        let patients = await caseLoadServer.query({
          UserIds: [this.currentUser.id],
          page: 1,
          pageSize: 1000000
        });
        patients = patients.records.map(x => x.patient.id);
        response = response.filter(x => patients.includes(x.patient.id));
      }
      this.newChatSelectOptions = [];

      let portalUsers = this.rooms?.map(x => x.users);

      response.forEach(item => {
        const isRegister =
          portalUsers.length > 0
            ? portalUsers.filter(
                x =>
                  x[0]._id == item.patient.id && x[1]._id == item.portalUser.id
              )
            : [];
        if (isRegister.length == 0) {
          this.newChatSelectOptions.push({
            value: item.portalUser.id,
            label: `${item.patient.mrn} - ${item.name} (${item.relationship})`,
            data: item
          });
        }
      });
    },
    addRoom() {
      if (!this.selectNewConversationOpen) {
        this.addNewRoom = !this.addNewRoom;
        setTimeout(() => {
          this.$refs.newChatSelect.open = true;
        }, 0);
        this.selectNewConversationOpen = true;
      } else {
        this.selectNewConversationOpen = false;
      }
    },
    closeAddRoom() {
      this.addNewRoom = false;
    }
  },

  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      currentTenant: state => state.systemSetting.currentTenant,
      signalRAppUrl: state => state.systemSetting.signalRAppUrl
    }),
    ...mapGetters("layout", ["hasCurrentUserPermission"]),
    userFormatted() {
      return this.currentUser ? this.formatUser(this.currentUser) : "";
    },
    screenHeight() {
      return "calc(100vh - 123px)";
    },
    selectNewRoomWidth() {
      return "calc(100vw - 77%)";
    }
  },
  watch: {
    showing: async function(value) {
      if (value) {
        this.currentUserId = this.currentUser.id;
        this.portalUsers = await portalServer.getAll();
        await this.fetchRooms();

        if (!this.signalRisConnected) {
          chatSignalRConnection.initialize("chat", this.signalRAppUrl);
          this.signalRisConnected = true;
          chatSignalRConnection.on("NewMainAppChatMessage", async result => {
            if (
              result.receivers.includes(this.currentUserId) &&
              result.tenantName === this.currentTenant
            ) {
              await this.fetchMessages({});
            }
          });
        }
      } else {
        this.currentUserId = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.window-container {
  width: 100%;
}

form {
  padding-bottom: 20px;
}

button {
  background: #1976d2;
  color: #fff;
  outline: none;
  cursor: pointer;
  border-radius: 4px;
  padding: 8px 12px;
  margin-left: 10px;
  border: none;
  font-size: 14px;
  transition: 0.3s;
  vertical-align: middle;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  &:disabled {
    cursor: initial;
    background: #c6c9cc;
    opacity: 0.6;
  }
}

.button-cancel {
  color: #a8aeb3;
  background: none;
  margin-left: 5px;
}

select {
  vertical-align: middle;
  height: 33px;
  width: 152px;
  font-size: 13px;
  margin: 0 !important;
}

.float-modal {
  position: fixed;
  z-index: 10;
  top: 140px;
  left: 35px;
  height: 100vh;
}
</style>
